import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const PythonCasting = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Data Transformation</div>
                <h1>Type Casting & Conversions</h1>
                <p className="hero-lead">
                    Master the art of transforming data. From simple numeric conversions to parsing complex strings,
                    binary encodings, and restructuring collections, understand how Python's "Universal Adaptor" mechanism works.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    In a perfect world, data would always arrive in the format we need. Integers would be integers,
                    and dates would be date objects. In the real world, however, we get numbers as strings from text files,
                    lists that contain duplicates, binary streams from networks, and floating-point noise when we need precise currency.
                </p>
                <p>
                    <strong>Casting</strong> is the process of converting a value from one data type to another.
                    Think of it as a <strong>Universal Adaptor</strong> for your data. Just as a travel adaptor allows
                    a US plug to fit into a UK socket, casting allows a String to plug into a Math operation, or a List
                    to plug into a Set operation.
                </p>
                <p>
                    Because Python is <strong>Strongly Typed</strong>, it refuses to guess what you mean. It won't
                    automatically add text to a number (like JavaScript might, resulting in "10" + 5 = "105"). It forces you to be explicit, which
                    prevents a massive category of silent bugs.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Big Three:</strong> Master <code>int()</code>, <code>float()</code>, and <code>str()</code>.</li>
                        <li><strong>Advanced Integers:</strong> Casting binary, hex, and octal strings.</li>
                        <li><strong>Text vs Bytes:</strong> The crucial <code>encode()</code> and <code>decode()</code> dance.</li>
                        <li><strong>Developer Vision:</strong> The difference between <code>str()</code> and <code>repr()</code>.</li>
                        <li><strong>Custom Casting:</strong> Making your own classes castable using Dunder methods.</li>
                        <li><strong>Collection Molding:</strong> Using casting to remove duplicates or freeze lists.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Implicit vs. Explicit Conversion</h2>
                <p>
                    Python handles conversions in two ways. Understanding the difference is key to writing clean code.
                </p>

                <h3>1. Implicit Conversion (Coercion)</h3>
                <p>
                    Python performs this automatically when it is 100% safe and data loss is impossible. The most common
                    example is mixing integers and floats.
                </p>
                <CodeBlock language="python" code={`# Safe Automatic Upgrade
x_int = 10      # Integer
y_float = 2.5   # Float

result = x_int + y_float
print(result)          # 12.5
print(type(result))    # <class 'float'>

# Python "promotes" the integer 10 to 10.0 to match the float.
# No precision is lost, so Python allows it.`} />

                <h3>2. Explicit Conversion (Casting)</h3>
                <p>
                    Python <strong>never</strong> implicitly converts between types if there's ambiguity or risk of validation error.
                    This is why <code>"5" + 5</code> throws an error. Do you want "55" (String) or 10 (Integer)? Python refuses to guess.
                    You must use a constructor function to tell it exactly what to do.
                </p>
            </div>

            <div className="content-section">
                <h2>The Big Three Constructors</h2>
                <p>
                    Three constructor functions handle 90% of everyday data transformation needs.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Constructor</th>
                                <th>Role</th>
                                <th>Can Handle</th>
                                <th>Fails On</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>int(x)</code></td>
                                <td>Makes Integers</td>
                                <td>Floats (truncates), Strings (whole numbers)</td>
                                <td>Strings with decimals ("4.5"), Text ("Hi")</td>
                            </tr>
                            <tr>
                                <td><code>float(x)</code></td>
                                <td>Makes Floats</td>
                                <td>Integers, Strings (decimals & scientific)</td>
                                <td>Text ("Hi")</td>
                            </tr>
                            <tr>
                                <td><code>str(x)</code></td>
                                <td>Makes Strings</td>
                                <td>Literally Anything (Everything has a string rep)</td>
                                <td>Nothing</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>1. The Integer Constructor: int()</h3>
                <p>
                    <code>int()</code> is strict. It truncates floats (rounding down towards zero) and demands
                    pure whole-number strings. However, it is smarter than it looks.
                </p>

                <h4>Parsing Bases (Binary/Hex)</h4>
                <p>
                    Did you know <code>int()</code> can act as a base converter? By default it assumes Base-10,
                    but you can decode Binary, Octal, or Hexadecimal strings.
                </p>

                <CodeBlock language="python" code={`# Standard Base-10
print(int("100"))      # 100

# Truncation behavior
print(int(3.99))       # 3 (Floors towards zero)

# Underscores in strings (Python 3.6+)
print(int("1_000_000")) # 1000000 (Readable!)

# Base Conversion
print(int("101", 2))   # 5  (Binary '101')
print(int("FF", 16))   # 255 (Hex 'FF')
print(int("77", 8))    # 63 (Octal '77')

#  The Common Trap
# print(int("3.14"))   # ValueError! It doesn't know how to handle the dot.

# The Fix
print(int(float("3.14"))) # 3 (String -&gt; Float -&gt; Int)`} />

                <h3>2. The Float Constructor: float()</h3>
                <p>
                    <code>float()</code> is more accommodating. It can handle scientific notation and special
                    values like Infinity.
                </p>
                <CodeBlock language="python" code={`print(float(5))       # 5.0
print(float("3.14"))  # 3.14
print(float("1e3"))   # 1000.0 (scientific notation for 10^3)

# Special Mathematical Values
infinity = float("inf")
nan = float("nan")    # Not a Number
print(infinity &gt; 10**100) # True`} />

                <h3>3. The String Constructor: str() vs repr()</h3>
                <p>
                    <code>str()</code> is the friendliest function. It can turn any object into a string representation,
                    usually for printing or logging. However, Python has a secret second way to convert to string: <code>repr()</code>.
                </p>
                <ul>
                    <li><strong>str(obj)</strong>: Readable, user-friendly text. (e.g., used by <code>print()</code>).</li>
                    <li><strong>repr(obj)</strong>: Unambiguous, developer-focused text. Often code that could recreate the object.</li>
                </ul>
                <CodeBlock language="python" code={`import datetime

now = datetime.datetime.now()

# User Friendly
print(str(now))   # 2023-10-25 14:30:00.123456

# Developer Friendly (Debugging)
print(repr(now))  # datetime.datetime(2023, 10, 25, 14, 30, 0, 123456)

s = "Hello\nWorld"
print(str(s))     
# Hello
# World

print(repr(s))    # 'Hello\\nWorld' (Shows the escape character)`} />
            </div>

            <div className="content-section">
                <h2>Deep Dive: Bytes and Encodings</h2>
                <p>
                    In the modern web, the most critical casting you will do is not between numbers, but between
                    <strong>Text</strong> (Unicode) and <strong>Data</strong> (Bytes).
                </p>
                <p>
                    In Python 3, <code>str</code> is abstract Unicode text. You can't send it over a network or save it to a file directly.
                    You must <strong>encode</strong> it into <code>bytes</code>. Conversely, effectively reading a file means
                    receiving <code>bytes</code> and <strong>decoding</strong> them into text.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Encode/Decode Dance</span>
                    </div>
                    <CodeBlock language="python" code={`# 1. Text (Abstract Idea)
text = "Caf "
print(type(text)) # <class 'str'>

#  You cannot save 'text' to a binary file directly.
# You must CAST it to bytes via encoding.

# 2. Encoding (Serialization)
# We cast string -&gt; bytes using UTF-8 standard
data = text.encode('utf-8')
print(type(data)) # <class 'bytes'>
print(data)       # b'Caf\xc3\xa9 \xe2\x98\x95' (The raw numbers)

# 3. Decoding (Deserialization)
# We cast bytes -&gt; string
decoded_text = data.decode('utf-8')
print(decoded_text) # "Caf "`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Boolean Constructor: Truthiness</h2>
                <p>
                    In Python, <strong>everything</strong> can be cast to a Boolean. This is extremely useful for
                    writing concise control flow logic.
                </p>
                <p>
                    The rule is simple: <strong>Empty/Zero/None is False. Everything else is True.</strong>
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Truthiness Chart</span>
                    </div>
                    <CodeBlock language="python" code={`# Falsy Values (evaluate to False)
print(bool(0))          # False (Zero)
print(bool(0.0))        # False (Zero float)
print(bool(""))         # False (Empty string)
print(bool([]))         # False (Empty list)
print(bool({}))         # False (Empty dict)
print(bool(None))       # False (The void)

# Truthy Values (evaluate to True)
print(bool(1))          # True
print(bool(-5))         # True (Any non-zero number)
print(bool("False"))    # True (Non-empty string!)
print(bool([0]))        # True (Non-empty container)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Custom Casting: The Dunder Methods</h2>
                <p>
                    Want your own classes to support casting? Python's object-oriented model allows you to define
                    how your objects behave when passed to <code>int()</code>, <code>str()</code>, or <code>bool()</code>.
                    This is done using "Dunder" (Double Underscore) methods.
                </p>

                <CodeBlock language="python" code={`class BitcoinWallet:
    def __init__(self, balance):
        self.balance = balance

    def __int__(self):
        """Called when int(wallet) is run"""
        return int(self.balance)

    def __str__(self):
        """Called when str(wallet) or print(wallet) is run"""
        return f"{self.balance} BTC"

    def __bool__(self):
        """Called when bool(wallet) or if wallet: is run"""
        return self.balance &gt; 0

my_wallet = BitcoinWallet(0.5)

print(int(my_wallet))   # 0 (Casts to int)
print(str(my_wallet))   # "0.5 BTC" (Casts to string)

if my_wallet:
    print("You have money!") # Uses __bool__`} />
            </div>

            <div className="content-section">
                <h2>Collection Molding: Reshaping Data</h2>
                <p>
                    Casting isn't just for scalars. You can cast between collection types to gain their superpowers.
                </p>

                <h3>1. Removes Duplicates (List -&gt; Set -&gt; List)</h3>
                <p>
                    The fastest way to deduplicate a list is to cast it to a Set (which forbids duplicates) and back.
                </p>
                <CodeBlock language="python" code={`raw_data = ["apple", "banana", "apple", "cherry", "banana"]

# transform
unique_data = list(set(raw_data))

print(unique_data) 
# ['banana', 'cherry', 'apple'] (Order is lost!)`} />

                <h3>2. Freezing Data (List -&gt; Tuple)</h3>
                <p>
                    Need to use a list as a dictionary key? You can't, because lists are mutable. Cast it to a tuple first!
                </p>
                <CodeBlock language="python" code={`coords = [10, 20]

# location_map = {coords: "Home"} #  TypeError: unhashable type: 'list'

frozen_coords = tuple(coords)
location_map = {frozen_coords: "Home"} # Works!`} />

                <h3>3. Zipping to Dictionary (List of Tuples -&gt; Dict)</h3>
                <p>
                    If you have paired data, <code>dict()</code> can snap it together intelligently.
                </p>
                <CodeBlock language="python" code={`pairs = [
    ("name", "Alice"),
    ("age", 30),
    ("role", "Engineer")
]

user_profile = dict(pairs)
print(user_profile["name"]) # "Alice"`} />
            </div>

            <div className="content-section real-world-section">
                <h2>Real-World Application: Safe Casting</h2>
                <p>
                    User input is messy. You naturally assume a user will enter a number for "age", but they might enter
                    "twenty" or "20 years". If you just run <code>int()</code>, your program crashes.
                </p>
                <p>
                    This pattern, known as EAFP (Easier to Ask Forgiveness than Permission), is the standard way
                    to handle casting in production.
                </p>

                <div className="use-case-grid">
                    <div className="use-case-card">
                        <h3> Fragile Code</h3>
                        <CodeBlock language="python" code={`def get_age():
    age_str = input("Enter age: ")
    # Crashes if user types "25ish"
    age = int(age_str)
    return age`} />
                    </div>

                    <div className="use-case-card">
                        <h3>Robust Code</h3>
                        <CodeBlock language="python" code={`def get_age():
    age_str = input("Enter age: ")
    try:
        # Try to cast
        return int(age_str)
    except ValueError:
        # Handle the failure gracefully
        print("Please enter a valid number (e.g., 25)")
        return None`} />
                    </div>
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & Takeaways</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>try/except ValueError</code> when converting user input.</li>
                            <li>Use <code>if my_list:</code> instead of <code>if len(my_list) {'>'} 0:</code> (Implicit Bool).</li>
                            <li>Use <code>set()</code> casting for quick deduplication.</li>
                            <li>Use <code>repr()</code> when debugging to see exact characters.</li>
                            <li>Remember to <code>decode()</code> bytes when reading from networks/files.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't cast unnecessarily (e.g., <code>str("hello")</code>).</li>
                            <li>Don't assume <code>set()</code> preserves order (it behaves randomly).</li>
                            <li>Don't use <code>eval()</code> to cast data (security risk!).</li>
                            <li>Don't rely on <code>int()</code> rounding (it always floors). use <code>round()</code> first.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonCasting;



